/**
 * Copy contract.
 *
 * Editorial copy keys for every user-facing string. Lane D delivers final values
 * in content/copy.md; this file is the engineer-consumable export and the
 * stable key registry.
 *
 * Lane B reads from this map for every visible string. Adding a string?
 * Add a key here first, then use COPY.YOUR_KEY in the component.
 *
 * Voice rules per DESIGN.md §8: ALL CAPS for commands, editorial sentence case
 * for body, no emojis, no exclamation marks.
 */

export const COPY = Object.freeze({
  // Brand
  BRAND_NAME: "MOGGED",

  // Navigation
  ENTER_CTA: "ENTER",
  RETURN_CTA: "RETURN",
  DISMISS_CTA: "DISMISS",
  RETRY_CTA: "RETRY",
  TRY_AGAIN_CTA: "TRY AGAIN",

  // Splash
  SPLASH_SUBTITLE: "Awaiting submission.",

  // Legal
  LEGAL_HEADLINE: "ENTRY REQUIRES ATTESTATION",
  LEGAL_BODY:
    "MOGGED is intended for visitors aged 16 and older. By proceeding you attest that you meet this requirement.",
  LEGAL_CTA: "I AM 16 OR OLDER",

  // Oracle — upload
  UPLOAD_PROMPT: "DROP PORTRAIT OR PRESS TO SUBMIT",
  UPLOAD_HOVER: "RELEASE TO SUBMIT",
  UPLOAD_SIZE_ERROR_HEADLINE: "PORTRAIT TOO LARGE",
  UPLOAD_SIZE_ERROR_SUBTITLE: "Submissions must remain under five megabytes.",
  UPLOAD_TYPE_ERROR_HEADLINE: "FORMAT NOT ACCEPTED",
  UPLOAD_TYPE_ERROR_SUBTITLE: "Submit a still image, JPEG or PNG.",

  // Oracle — analyzing
  ANALYZING_LABEL: "ANALYZING",
  ANALYZING_BODY: "The portrait is under review.",

  // Oracle — verdict
  VERDICT_HEADLINE: "ANALYSIS",
  VERDICT_VECTORS_HEADLINE: "VECTORS",
  VERDICT_VECTORS_SUBTITLE: "The portrait's measured composition.",
  VERDICT_RECOMMENDATIONS_HEADLINE: "REFINE",
  VERDICT_RECOMMENDATIONS_SUBTITLE:
    "Three vectors selected for measured improvement.",
  VERDICT_PERCENTILE_LABEL: "GLOBAL PERCENTILE",
  VERDICT_PERCENTILE_NOTE:
    "Calibrated against the reference panel. A relative reading, not a verdict.",
  VERDICT_GEOMETRY_HEADLINE: "STRUCTURE",
  VERDICT_GEOMETRY_SUBTITLE:
    "Measured proportions: what reads strong, and what's addressable.",
  VERDICT_GEOMETRY_NOTE: "Frontal 2D estimates. A diagnostic read, not a verdict.",
  GEOMETRY_BAND_STRONG: "STRONG",
  GEOMETRY_BAND_ADDRESSABLE: "REFINE",

  // Verdict — score categories (taxonomy panel)
  VERDICT_CATEGORIES_HEADLINE: "WHAT YOU CAN CHANGE",
  VERDICT_CATEGORIES_SUBTITLE:
    "Each trait, scored, and whether your plan can move it.",
  VERDICT_GLANCE_HEADER: "SCORE SUMMARY",
  VERDICT_METRICS_HEADLINE: "SCAN METRICS",
  VERDICT_METRICS_NOTE: "Measured this scan.",
  VERDICT_ROUTINE_REF_HEADLINE: "HOW YOUR ROUTINE IS PERFORMING",
  VERDICT_ROUTINE_REF_SUBTITLE:
    "Your plan and how each product is working between scans.",
  VERDICT_OPEN_ROUTINE_CTA: "OPEN ROUTINE",

  // Routine page
  ROUTINE_HEADLINE: "ROUTINE",
  ROUTINE_SUBTITLE:
    "Your standing plan. Strength climbs only when you confirm you're using it.",
  ROUTINE_EMPTY_HEADLINE: "NO ROUTINE YET",
  ROUTINE_EMPTY_SUBTITLE:
    "Capture a scan and the Oracle assembles your personalized regimen.",
  ROUTINE_DISCLAIMER:
    "MOGGED offers general cosmetic suggestions, not medical advice or diagnosis. Everything here is a direction to consider; consult a professional before starting something new. Rx-strength steps route you to a clinician rather than a product.",

  // Scan-complete reveal modal
  REVEAL_ANALYZING: "ANALYZING",
  REVEAL_ANALYZING_NOTE: "Compiling facial vectors…",
  REVEAL_SCORE_LABEL: "MOGGED SCORE",
  REVEAL_DONE_HEADLINE: "YOUR RESULTS ARE IN",
  REVEAL_ARCHIVED_NOTE: "Archived to your Vault · your routine has been refreshed.",
  REVEAL_VIEW_SCORE_CTA: "VIEW FULL SCORE",
  REVEAL_REVIEW_ROUTINE_CTA: "REVIEW ROUTINE",

  // Rejection states
  REJECT_NO_FACE_HEADLINE: "NO PORTRAIT DETECTED",
  REJECT_NO_FACE_SUBTITLE: "Better lighting and a forward-facing pose, please.",

  REJECT_MULTIPLE_FACES_HEADLINE: "ONE PORTRAIT PER SCAN",
  REJECT_MULTIPLE_FACES_SUBTITLE: "Submit a portrait that shows only you.",

  REJECT_UNDER_16_HEADLINE: "ENTRY DECLINED",
  REJECT_UNDER_16_SUBTITLE:
    "MOGGED is reserved for visitors aged 16 and older.",

  REJECT_NSFW_HEADLINE: "SUBMISSION INELIGIBLE",
  REJECT_NSFW_SUBTITLE: "Submit a portrait of your face.",

  REJECT_MODEL_REFUSED_HEADLINE: "ANALYSIS UNAVAILABLE",
  REJECT_MODEL_REFUSED_SUBTITLE:
    "The analysis could not be completed for this portrait. Try another submission.",

  REJECT_NETWORK_HEADLINE: "THE GALLERY IS SHUTTERED",
  REJECT_NETWORK_SUBTITLE: "Reconnection in progress.",

  // Client-side rate limit (attempt cap in Create.js — not a server rejection)
  REJECT_RATE_LIMIT_SUBTITLE:
    "The Oracle pauses between repeated submissions. Wait a moment, then try again.",

  // Verified-email gate (EMAIL_UNVERIFIED — not a content rejection)
  VERIFY_EMAIL_HEADLINE: "CONFIRM YOUR EMAIL",
  VERIFY_EMAIL_SUBTITLE:
    "The Oracle answers confirmed members only. Follow the link sent to your inbox, then return.",
  VERIFY_EMAIL_RESEND_CTA: "RESEND CONFIRMATION",
  VERIFY_EMAIL_RESENT_NOTE: "CONFIRMATION DISPATCHED. CHECK YOUR INBOX.",
  VERIFY_EMAIL_CONFIRMED_CTA: "I'VE CONFIRMED",
  VERIFY_EMAIL_NOT_YET_NOTE: "NOT YET CONFIRMED. FOLLOW THE EMAIL LINK FIRST.",

  // Empty states
  EMPTY_VECTOR_CATALOG_HEADLINE: "THE CATALOG IS QUIET",
  EMPTY_VECTOR_CATALOG_SUBTITLE: "Curated recommendations arrive shortly.",

  // Sign-in
  SIGN_IN_HEADLINE: "ENTRY REQUIRES ATTESTATION",
  SIGN_IN_SUBTITLE: "Verify yourself before submitting a portrait.",
  SIGN_IN_GOOGLE_CTA: "CONTINUE WITH GOOGLE",
  SIGN_IN_EMAIL_LABEL: "EMAIL",
  SIGN_IN_PASSWORD_LABEL: "PASSWORD",
  SIGN_IN_SUBMIT_CTA: "SIGN IN",
  SIGN_IN_TOGGLE_TO_SIGNUP: "NO ACCOUNT. BEGIN",
  SIGN_UP_HEADLINE: "ESTABLISH MEMBERSHIP",
  SIGN_UP_SUBTITLE: "An account is required for the Vault and the Glowup.",
  SIGN_UP_SUBMIT_CTA: "CREATE ACCOUNT",
  SIGN_UP_TOGGLE_TO_SIGNIN: "ALREADY A MEMBER. RETURN",
  SIGN_IN_DIVIDER: "OR",
  SIGN_IN_ERROR_HEADLINE: "UNABLE TO PROCEED",
  SIGN_IN_INVALID_EMAIL:
    "Provide a valid email address and a password of at least six characters.",
  SIGN_IN_GENERIC_ERROR:
    "Sign-in could not be completed. Try again or use another method.",
  SIGN_OUT_CTA: "SIGN OUT",

  // Missing-user-doc recovery (src/components/auth/UserDocRecovery.js). Shown
  // when the signed-in user has no Firestore record — historically a permanent
  // full-screen spinner for ~100 accounts.
  RECOVERY_FAILED_HEADLINE: "YOUR RECORD DID NOT OPEN",
  RECOVERY_FAILED_SUBTITLE:
    "Your membership record could not be restored. Retry, or sign out and back in.",

  // Marketing — Sign-In / Splash hero (DESIGN.md §16.1)
  MARKETING_WORDMARK: "MOGGED",
  MARKETING_TAGLINE_1: "ARCHIVED FACE SCANS.",
  MARKETING_TAGLINE_2: "PERSONALIZED GROWTH.",
  MARKETING_BODY:
    "Precision scans. Real feedback. Track your progress and become your best version.",
  MARKETING_MANIFESTO: "UNLOCK YOUR POTENTIAL",

  // Recency copy for the last reading, shared by Home's score hero and the
  // reveal modal. These briefly backed a returning-member summary on the scan
  // surface; that block moved to /home and Create went back to showing the
  // steps, so only the keys with live consumers remain.
  RETURNING_TODAY: "Taken today",
  RETURNING_YESTERDAY: "Taken yesterday",
  RETURNING_DAYS_SUFFIX: "days ago",
  RETURNING_FIRST_SCAN_NOTE: "Your first reading. The next one shows how far you have moved.",

  // Home — the signed-in landing surface (pages/Home.js). Museum-text register:
  // labels and CTAs in caps, never sentence-case marketing copy.
  HOME_TITLE: "HOME",
  HOME_SUBTITLE: "Where you stand, and what moves next.",
  HOME_LAST_SCAN_LABEL: "LAST SCAN",
  HOME_TREND_EYEBROW: "YOUR PROGRESS",
  HOME_TREND_NEEDS_SECOND:
    "One reading on record. Take another and this chart shows how far you have moved.",
  HOME_CATEGORIES_EYEBROW: "STRONGEST READINGS",
  HOME_VERDICT_CTA: "OPEN FULL VERDICT",
  // Sealed latest reading. Points at Explore, not the verdict page — that page
  // would only show the same gate one navigation later.
  //
  // Says UNLOCK rather than OPEN: the reading is not openable yet, and a button
  // that promises otherwise is what makes a gate feel like a bug. It previously
  // said EARN, which was accurate but put the cost before the payoff — UNLOCK
  // names the outcome, and the progress bar beside it already shows the price.
  // The sealed-scan notification email uses the same words for the same action.
  HOME_SEALED_CTA: "UNLOCK THIS READING",
  HOME_ROUTINE_EYEBROW: "TONIGHT'S ROUTINE",
  HOME_ROUTINE_STEPS_SUFFIX: "STEPS",
  HOME_ROUTINE_STEP_SUFFIX: "STEP",
  HOME_ROUTINE_CTA: "OPEN ROUTINE",
  HOME_ROUTINE_EMPTY: "No evening steps yet. Your routine is built from your reading.",
  HOME_EMPTY_TITLE: "NO READING ON RECORD",
  HOME_EMPTY_BODY:
    "Submit a portrait for analysis. Your score, your categories and your routine all follow from it.",
  HOME_EMPTY_CTA: "SUBMIT PORTRAIT",
  HOME_RESCAN_CTA: "SUBMIT NEW PORTRAIT",

  // Signed-out mobile splash CTAs (components/oracle/MobileIntro.js).
  // JOIN opens the sign-up tab; the secondary opens log-in. Returning members
  // need a way in that is not a hunt — returning direct visitors are a
  // disproportionate share of the affiliate clicks the product earns.
  //
  // The example-verdict copy that used to live here (an overall score plus
  // scored vectors) is gone with the component: numbers over the portrait
  // fought the face, and an invented score is the one thing on that page a
  // visitor cannot verify.
  // "GET STARTED", not "SUBMIT YOUR PORTRAIT": this button opens the sign-up
  // tab, it does not open a file picker. Naming it after the scan promised an
  // action the next screen does not perform, on the page that receives 87% of
  // all sessions. The scan CTAs keep the editorial phrasing where they actually
  // submit a portrait (ORACLE_SUBMIT_LABEL, HOME_EMPTY_CTA, ABOUT_CTA).
  INTRO_JOIN_CTA: "GET STARTED",
  INTRO_MEMBER_PREFIX: "Already a member?",
  INTRO_MEMBER_CTA: "Log in",

  // Accessible page title + heading for the signed-out landing surface.
  // `/` and /sign-in previously had no <h1> and inherited the generic global
  // <title>, on the page that receives 87% of all sessions.
  SIGN_IN_PAGE_TITLE: "MOGGED — AI face analysis and a routine built for it",
  SIGN_IN_H1: "MOGGED — archived face scans and personalized growth",
  MARKETING_FEATURE_1_NAME: "PRECISION SCANS",
  MARKETING_FEATURE_1_BODY: "Advanced AI face analysis.",
  MARKETING_FEATURE_2_NAME: "PERSONALIZED GROWTH",
  MARKETING_FEATURE_2_BODY: "Actionable feedback over time.",
  MARKETING_FEATURE_3_NAME: "PRIVATE & ANONYMOUS",
  MARKETING_FEATURE_3_BODY: "Your data. Your journey.",
  MARKETING_PULL_QUOTE:
    "The privilege of a lifetime is to become who you truly are.",
  MARKETING_PULL_QUOTE_ATTRIBUTION: "CARL JUNG",
  MARKETING_FOOTER_PRIVATE_NAME: "PRIVATE BY DESIGN",
  MARKETING_FOOTER_PRIVATE_BODY: "Scans are private to your account.",
  MARKETING_FOOTER_COMMUNITY_NAME: "ANONYMOUS COMMUNITY",
  MARKETING_FOOTER_COMMUNITY_BODY: "Join thousands improving in silence.",
  MARKETING_FOOTER_TRACK_NAME: "TRACK YOUR PROGRESS",
  MARKETING_FOOTER_TRACK_BODY: "Scans, feedback, and history in one place.",
  MARKETING_FOOTER_SECURE_NAME: "PERSONALIZED FEEDBACK",
  MARKETING_FOOTER_SECURE_BODY: "Guidance tuned to your face.",
  AUTH_TAB_LOG_IN: "LOG IN",
  AUTH_TAB_SIGN_UP: "SIGN UP",
  AUTH_SUBMIT_LOG_IN: "LOG IN TO MOGGED",
  AUTH_SUBMIT_SIGN_UP: "CREATE MEMBERSHIP",
  AUTH_FORGOT_PASSWORD: "Forgot password?",
  AUTH_OR_CONTINUE_WITH: "OR CONTINUE WITH",
  AUTH_GOOGLE_CTA: "CONTINUE WITH GOOGLE",
  AUTH_APPLE_CTA: "CONTINUE WITH APPLE",
  AUTH_TERMS_PROSE: "By continuing, you agree to our",
  AUTH_TERMS_LINK_TOS: "Terms of Service",
  AUTH_TERMS_LINK_PRIVACY: "Privacy Policy",
  AUTH_TERMS_CONJUNCTION: "and",
  AUTH_EMAIL_PLACEHOLDER: "Enter your email",
  AUTH_PASSWORD_PLACEHOLDER: "Enter your password",

  // Oracle — submit / analyze (DESIGN.md §16.2)
  ORACLE_SUBMIT_LABEL: "SUBMIT PORTRAIT",
  ORACLE_READY_TO_SUBMIT_LABEL: "READY TO SUBMIT",
  ORACLE_HELPER: "Front-facing portrait, neutral expression, even lighting.",
  ORACLE_ANALYZE_CTA: "ANALYZE PORTRAIT",
  ORACLE_RESUBMIT_CTA: "RESUBMIT",
  ORACLE_PIPELINE_STEP_AGE: "AGE VERIFICATION",
  ORACLE_PIPELINE_STEP_SAFETY: "SAFETY CHECK",
  ORACLE_PIPELINE_STEP_SCORING: "AESTHETIC SCORING",
  ORACLE_PIPELINE_STEP_REGIMEN: "REGIMEN MAPPING",

  // Glowups — judge feed (DESIGN.md §16.4)
  JUDGE_FEED_HEADLINE: "GLOWUPS",
  JUDGE_FEED_SUBTITLE: "Anonymous glowups. Real progress. Positive encouragement.",
  JUDGE_FILTER_ANON: "ANONYMOUS COMMUNITY",
  JUDGE_FILTER_ENCOURAGEMENT: "ENCOURAGEMENT ONLY",
  JUDGE_FILTER_POSITIVE: "POSITIVE VOTES",
  JUDGE_SUMMARY_LABEL: "ENCOURAGEMENT VOTES",
  JUDGE_SUMMARY_PROMPT: "Spread positivity. Celebrate progress.",
  JUDGE_SUMMARY_TAG_1: "Strengthens confidence",
  JUDGE_SUMMARY_TAG_2: "Creates impact",
  JUDGE_SUMMARY_TAG_3: "More positive presence",
  JUDGE_CARD_ANON_NAME: "Anonymous Member",
  JUDGE_CARD_ANON_SUB: "Profile Hidden",
  JUDGE_PREV_LABEL: "PREVIOUS",
  JUDGE_NEXT_LABEL: "NEXT",
  JUDGE_SCROLL_HINT: "SCROLL FOR NEXT GLOWUP",
  ENCOURAGE_HEADER: "LEAVE ENCOURAGEMENT",
  ENCOURAGE_HELPER:
    "Select the areas where you see the most improvement. Anonymous encouragement only.",
  ENCOURAGE_SEND_CTA: "SEND ENCOURAGEMENT",
  ENCOURAGE_SENT_CONFIRM: "ENCOURAGEMENT SENT",

  // The in-flight label on every submit button, paired with ButtonSpinner.
  // One string for all of them: a button that changes its own verb mid-action
  // ("PUBLISHING…", "SENDING…") makes the user re-read it to find out whether
  // anything is happening. Present participle in caps matches the register the
  // scan flow already uses for ANALYZING, and DESIGN.md §8.1 rules out the
  // "Loading..." phrasing outright.
  BUTTON_SUBMITTING: "SUBMITTING",
  ENCOURAGE_SKIP_CTA: "SKIP / NEXT GLOWUP",
  // Keyed by the Oracle feedback taxonomy category keys (contracts/taxonomy.js)
  // — the 6 modifiable categories Glowup is judged on.
  ENCOURAGE_VECTOR_SKIN_QUALITY_DESC: "Clearer, healthier skin.",
  ENCOURAGE_VECTOR_HAIR_SCALP_DESC: "Fuller, healthier hair.",
  ENCOURAGE_VECTOR_FACIAL_HAIR_DESC: "Cleaner, fuller beard.",
  ENCOURAGE_VECTOR_BROWS_LASHES_DESC: "Better brows & lashes.",
  ENCOURAGE_VECTOR_MOUTH_SMILE_DESC: "Brighter, healthier smile.",
  ENCOURAGE_VECTOR_DEFINITION_DESC: "Sharper facial definition.",
  ENCOURAGE_VECTOR_HOLISTIC_DESC: "Big glowup overall.",

  // Glowup own-view (DESIGN.md §16.3)
  GLOWUP_OWN_RECEIVED_HEADER: "ENCOURAGEMENT RECEIVED",
  GLOWUP_OWN_NO_ENCOURAGEMENT:
    "Submit your glowup to the feed and encouragement will arrive over time.",
  GLOWUP_OWN_KEBAB_LABEL: "More options",
  GLOWUP_OWN_KEBAB_DELETE: "Delete glowup",
  GLOWUP_OWN_KEBAB_SHARE: "Share glowup",
  GLOWUP_OWN_KEBAB_REPORT: "Report issue",
  GLOWUP_OWN_TIMESPAN_PREFIX: "",
  GLOWUP_OWN_TIMESPAN_SUFFIX: "GLOWUP",

  // Glowup — creator
  GLOWUP_CREATE_HEADLINE: "ASSEMBLE A GLOWUP",
  GLOWUP_CREATE_SUBTITLE:
    "Select two portraits taken at different times for peer review.",
  GLOWUP_SIDE_BEFORE_LABEL: "BEFORE",
  GLOWUP_SIDE_AFTER_LABEL: "AFTER",
  GLOWUP_SIDE_VAULT_TAB: "FROM VAULT",
  GLOWUP_SIDE_FRESH_TAB: "FRESH UPLOAD",
  GLOWUP_VAULT_COMING_SOON_HEADLINE: "VAULT ARRIVES SHORTLY",
  GLOWUP_VAULT_COMING_SOON_SUBTITLE:
    "Selecting from prior portraits will be enabled once the Vault opens. Submit fresh portraits in the meantime.",
  GLOWUP_TAKEN_AT_PROMPT_HEADLINE: "WHEN WAS THIS PORTRAIT TAKEN",
  GLOWUP_TAKEN_AT_PROMPT_SUBTITLE:
    "An approximate date is sufficient. The Oracle uses it to order the two portraits.",
  GLOWUP_TAKEN_AT_INVALID:
    "The date must be a past date in the format YYYY-MM-DD.",
  GLOWUP_TAKEN_AT_FUTURE: "The date cannot be in the future.",
  GLOWUP_TAKEN_AT_ORDER:
    "The before-portrait date must be earlier than the after-portrait date.",
  GLOWUP_SUBMIT_CTA: "PRESENT GLOWUP",

  // Glowup — judge
  JUDGE_HEADLINE: "REVIEW THE GLOWUPS",
  JUDGE_SUBTITLE:
    "Mark improvements you see. Optionally score each on a scale of 1 to 10.",
  JUDGE_DECK_EMPTY_HEADLINE: "NO NEW GLOWUPS",
  JUDGE_DECK_EMPTY_SUBTITLE:
    "Fresh glowups arrive shortly. Return to submit a portrait of your own.",
  JUDGE_VECTOR_PROMPT: "WHICH VECTORS IMPROVED",
  JUDGE_HOLISTIC_LABEL: "BIGGEST OVERALL",
  JUDGE_SUBMIT_CTA: "SUBMIT FEEDBACK",
  JUDGE_PASS_CTA: "PASS",
  JUDGE_AFTER_VOTE: "FEEDBACK RECORDED",
  JUDGE_QUOTA_LABEL: "FEEDBACK",
  JUDGE_TIME_GAP_WEEK: "1 WEEK",
  JUDGE_TIME_GAP_WEEKS: "WEEKS",
  JUDGE_TIME_GAP_MONTHS: "MONTHS",
  JUDGE_TIME_GAP_YEARS: "YEARS",
  JUDGE_TIME_GAP_PERIOD: "PERIOD",

  // Glowup — owner detail
  GLOWUP_DETAIL_HEADLINE: "YOUR GLOWUP",
  GLOWUP_PEER_SCORE_HEADLINE: "PEER SCORE",
  GLOWUP_PEER_SCORE_SUBTITLE:
    "The crowd's reading of what improved most.",
  GLOWUP_PEER_QUOTA_LOCKED:
    "Give feedback on five Glowups to unlock your peer score.",
  GLOWUP_NO_JUDGMENTS_YET:
    "No feedback recorded yet. Check back shortly.",

  // Glowup — own list
  GLOWUPS_INDEX_HEADLINE: "YOUR GLOWUPS",
  GLOWUPS_INDEX_EMPTY_HEADLINE: "NO GLOWUPS YET",
  GLOWUPS_INDEX_EMPTY_SUBTITLE:
    "Two portraits taken months apart make a Glowup. Begin one.",
  GLOWUPS_INDEX_CREATE_CTA: "BEGIN A GLOWUP",

  // Vault (Phase 1, Lane A)
  VAULT_HEADER: "THE VAULT",
  VAULT_EMPTY_HEADLINE: "THE VAULT IS EMPTY",
  VAULT_EMPTY_SUBTITLE: "Submit a portrait.",
  VAULT_ENTRY_NOT_YOURS_HEADLINE: "THIS PORTRAIT IS NOT YOURS",
  VAULT_ENTRY_NOT_YOURS_SUBTITLE: "Only the submitter may view this record.",
  VAULT_ENTRY_DISSOLVED_HEADLINE: "THIS PORTRAIT HAS DISSOLVED",
  VAULT_ENTRY_DISSOLVED_SUBTITLE: "No record remains in the Vault.",
  VAULT_COMPARE_CTA: "COMPARE",
  VAULT_COMPARE_PROMPT_HEADLINE: "SELECT A SECOND PORTRAIT",
  VAULT_COMPARE_PROMPT_SUBTITLE:
    "Pick another portrait from the Vault to view the deltas.",
  VAULT_COMPARE_EMPTY_HEADLINE: "NOTHING TO COMPARE",
  VAULT_COMPARE_EMPTY_SUBTITLE:
    "Submit additional portraits to begin a comparison.",
  VAULT_LOAD_MORE_CTA: "LOAD MORE",
  VAULT_SUBTITLE: "Archived face scans and personalized feedback.",
  VAULT_FOCUS_TAG: "FOCUS SCAN",
  VAULT_SCAN_TITLE: "SCAN",
  VAULT_BACK_LABEL: "Back to Vault",
  VAULT_MOST_IMPROVED_TAG: "MOST IMPROVED",
  VAULT_FACIAL_FEEDBACK_HEADER: "FACIAL FEEDBACK",
  VAULT_FILTER_CTA: "FILTER",
  VAULT_EXPORT_CTA: "EXPORT",
  VAULT_SHARE_CTA: "SHARE SCAN",
  VAULT_VIEW_3D_CTA: "VIEW 3D",
  VAULT_SCAN_SCORE_LABEL: "SCAN SCORE",
  VAULT_REGIMEN_HEADER: "YOUR PERSONALIZED REGIMEN",
  VAULT_REGIMEN_SUBTITLE: "Curated for your goals and scan results.",
  VAULT_VIEW_FULL_REGIMEN_CTA: "VIEW FULL REGIMEN PLAN",
  VAULT_DEFAULT_COACHING_SUMMARY:
    "Consistent refinement creates quiet confidence.",
  VAULT_PAGINATION_SUFFIX: "SCANS",

  // Onboarding — mandatory 4-step wizard (age → gender → handle → terms).
  // Headlines/subtitles are sentence-case serif per the design; chrome
  // (step label, buttons) stays ALL-CAPS editorial per DESIGN.md §8.
  ONBOARDING_STEP_WORD: "STEP",
  ONBOARDING_OF_WORD: "OF",
  ONBOARDING_CONTINUE_CTA: "CONTINUE",
  ONBOARDING_BACK_CTA: "BACK",
  ONBOARDING_ENTER_CTA: "ENTER MOGGED",
  ONBOARDING_GENERIC_ERROR:
    "We couldn't save your details. Check your connection and try again.",

  // Step 1 — age
  ONBOARDING_AGE_HEADLINE: "How old are you?",
  ONBOARDING_AGE_SUBTITLE: "We calibrate your scan benchmarks by age.",
  ONBOARDING_AGE_UNIT: "years",

  // Step 2 — gender
  ONBOARDING_GENDER_HEADLINE: "Gender",
  ONBOARDING_GENDER_SUBTITLE: "Used only to tune your analysis. Always private.",

  // Step 3 — handle
  ONBOARDING_HANDLE_HEADLINE: "Choose your handle",
  ONBOARDING_HANDLE_SUBTITLE:
    "This is all anyone sees. Your real identity stays hidden.",
  ONBOARDING_HANDLE_PLACEHOLDER: "yourhandle",
  ONBOARDING_HANDLE_APPEARS_PREFIX: "Appears as",
  ONBOARDING_HANDLE_APPEARS_SUFFIX: "across the community.",
  ONBOARDING_HANDLE_CHECKING: "Checking availability…",
  ONBOARDING_HANDLE_TAKEN: "That handle is already taken.",
  ONBOARDING_HANDLE_AVAILABLE: "Handle available.",
  ONBOARDING_HANDLE_CHECK_FAILED:
    "Couldn't check that handle right now. Check your connection and try again.",

  // Step 4 — terms
  ONBOARDING_TERMS_HEADLINE: "One last thing",
  ONBOARDING_TERMS_SUBTITLE: "Review and agree before your archive opens.",
  ONBOARDING_TERMS_PRIVACY_NOTE:
    "Your scans and photos stay private and are never shared without your consent.",
  ONBOARDING_TERMS_MEDICAL_NOTE:
    "MOGGED offers cosmetic guidance, not medical advice or diagnosis.",
  ONBOARDING_TERMS_AGREE_PREFIX: "I agree to the",
  ONBOARDING_TERMS_LINK_TOS: "Terms of Service",
  ONBOARDING_TERMS_CONJUNCTION: "and",
  ONBOARDING_TERMS_LINK_PRIVACY: "Privacy Policy",

  // Legal modal (ToS + Privacy shown over the onboarding card and auth surface).
  ONBOARDING_LEGAL_CLOSE: "Close",
  // Bump this date whenever LEGAL_TOS_SECTIONS / LEGAL_PRIVACY_SECTIONS change —
  // the privacy policy tells users this date reflects the latest revision.
  LEGAL_LAST_UPDATED: "Last updated July 13, 2026.",
  LEGAL_TOS_SECTIONS: [
    {
      heading: "Acceptance of terms",
      body: "These Terms of Service govern your access to and use of MOGGED (the “Service”), operated by the MOGGED team (“we,” “us”). By creating an account or using the Service, you agree to these Terms and to our Privacy Policy. If you do not agree, do not use the Service.",
    },
    {
      heading: "Eligibility",
      body: "The Service is available only to individuals aged 16 or older. You represent that you meet this requirement. In addition to your attestation, we apply automated age-estimation screening to submitted portraits and may reject submissions or suspend accounts we reasonably believe belong to users under 16.",
    },
    {
      heading: "The service",
      body: "MOGGED provides AI-generated aesthetic analysis. When you submit a portrait, automated systems, including third-party machine-learning models, evaluate it and return scores, observations, and a personalized routine of suggested cosmetic products and habits. These outputs are automated, inherently subjective opinions provided for informational and self-improvement purposes. We do not warrant that any score, observation, or recommendation is accurate, complete, or suited to you.",
    },
    {
      heading: "Not medical advice",
      body: "The Service offers general cosmetic guidance only. Nothing in the Service constitutes medical advice, diagnosis, or treatment, and no clinician-patient relationship is formed. Consult a qualified professional before acting on any recommendation; suggestions involving prescription-strength products direct you to a clinician rather than a product.",
    },
    {
      heading: "Your content and license",
      body: "You retain ownership of the portraits and other content you submit. You grant us a limited, worldwide, royalty-free license to host, reproduce, and process your content, including transmitting it to the service providers that perform the analysis, solely to operate, provide, and improve the Service and to display results back to you. For content shared through the Service — a Glowup you submit, or your scans while portrait sharing is enabled in your account settings — this license also covers displaying anonymized versions of your portraits to other signed-in members as described below.",
    },
    {
      // ⚠ THE GLOWUP REMOVAL LINE BELOW STAYS ROUTED THROUGH SUPPORT.
      //
      // Account deletion now erases Glowups along with everything else, but
      // there is still no way to remove ONE Glowup while keeping the account:
      // the kebab item in GlowupDetail.js is a button with no onClick and
      // glowupFunctions/ has no delete function. Do not "modernise" this
      // sentence to promise self-serve removal until that ships — it is a
      // commitment on a crawlable page, and the deletion cascade does not
      // satisfy a per-Glowup request.
      heading: "Sharing and community features",
      body: "Two features can show your portraits to other signed-in members. Submitting a Glowup shares a before-and-after pair for encouragement votes and improvement feedback, and happens only when you submit one. Portrait sharing allows other signed-in members to give structured feedback on your scans; it is enabled by default, and you may disable it at any time in your account settings. Disabling it removes your scans from other members' decks. In both cases the portrait is shown with an anonymizing bar placed across the eyes, and no handle, name, or account identifier is attached to it. You may request removal of a Glowup you submitted at any time by contacting support@mogged.app. Community feedback is limited to the structured, encouragement-oriented options the Service provides.",
    },
    {
      heading: "Acceptable use",
      body: "You agree to submit only images of your own face; not to submit images of any other person, any person under 16, or any unlawful, sexually explicit, or infringing material; not to misrepresent your age or identity; and not to interfere with the Service, including attempting to manipulate scores or automated screening.",
    },
    // Rewritten 2026-08-05: this section described the offerwall (credits earned
    // by completing third-party offers), which was removed along with
    // rewardFunctions/offerwallPostback.js.
    //
    // ⚠ MUST BE REWRITTEN AGAIN BEFORE THE PAYWALL IS ENABLED. A paid tier needs
    // terms this section does not contain: the trial length and that a card is
    // required, the renewal and cancellation mechanics, refund handling, and —
    // because Stripe Managed Payments makes Stripe the merchant of record —
    // that the seller of record is not MOGGED, that the statement descriptor
    // and receipts come from Link, and that some countries cannot subscribe.
    // Counsel sign-off is already an open TODO for this whole document.
    {
      heading: "Scans",
      body: "Scanning is free and is not limited to a set number per day. A completed analysis is held until you unlock it, either by giving feedback on other members' portraits or by starting a membership. Scan limits, unlock requirements, and the features included at each tier may change; we will describe the current terms in the Service.",
    },
    {
      heading: "Advertising and affiliate links",
      body: "The Service displays third-party advertising and product recommendations that use affiliate links, and we may earn a commission on qualifying purchases made through them. Linked merchants, products, and offers are controlled by third parties; we do not endorse or warrant them, and your purchases from them are solely between you and the merchant.",
    },
    {
      heading: "Termination",
      body: "You may stop using the Service and delete your account at any time from your account settings. We may suspend or terminate your access if you violate these Terms, if required by law, or to protect the Service or its users. Provisions that by their nature should survive termination, including licenses needed for legal compliance, disclaimers, and limitations of liability, survive.",
    },
    {
      heading: "Disclaimers and limitation of liability",
      body: "The Service is provided “as is” and “as available,” without warranties of any kind, express or implied. To the maximum extent permitted by law, we are not liable for indirect, incidental, special, consequential, or punitive damages, or for decisions you make in reliance on AI-generated output, and our aggregate liability arising out of the Service will not exceed fifty U.S. dollars (USD $50).",
    },
    {
      heading: "Changes to these terms",
      body: "We may update these Terms from time to time. Material changes will be communicated in-app, and your continued use of the Service after a change takes effect constitutes acceptance of the revised Terms.",
    },
    {
      heading: "Contact",
      body: "Questions about these Terms can be directed to support@mogged.app.",
    },
  ],
  LEGAL_PRIVACY_SECTIONS: [
    {
      heading: "Scope",
      body: "This Privacy Policy describes how MOGGED (“we,” “us”) collects, uses, and shares personal information when you use the Service. By using the Service you consent to the practices described here.",
    },
    {
      heading: "Information you provide",
      body: "Account information (your email address and password, or your third-party sign-in identity such as Google); profile details collected during onboarding (age, gender, and your chosen handle); the face portraits you submit for scans and Glowups, together with the approximate dates you supply for them.",
    },
    {
      heading: "Information collected automatically",
      body: "Usage and analytics data such as pages viewed, feature events, and marketing-campaign attribution parameters, collected through third-party analytics services, along with basic device and browser information. Our advertising partners may set cookies or similar identifiers to serve and measure ads.",
    },
    {
      heading: "How portraits are processed",
      body: "To deliver your results, submitted portraits are transmitted to third-party service providers acting on our behalf, which perform face detection, age estimation, aesthetic analysis, and content-safety screening. Portraits that fail screening are rejected and the uploaded image is deleted.",
    },
    {
      heading: "How we use information",
      body: "We use your information to generate your scores, summaries, and personalized routine; to calibrate analysis by the age and gender you provide; to maintain your Vault and Glowup history; to count the feedback you give and unlock your analyses; to send transactional email such as verification and password-reset messages and occasional reminder emails; to measure and improve the Service; and to detect and prevent abuse.",
    },
    {
      heading: "Sharing",
      body: "Your personal information is shared only with third-party service providers that process it on our behalf, such as hosting, storage, image analysis, image generation, payment processing, analytics, and email-delivery providers; and where disclosure is required by law or necessary to protect the Service or its users.",
    },
    {
      heading: "What other members see",
      body: "Your scores and Vault are private to your account. Two features, and only these two, can show a portrait to another member. Submitting a Glowup displays a before-and-after pair to other signed-in members for encouragement votes and improvement feedback, and occurs only when you submit one. Portrait sharing allows other signed-in members to give structured feedback on your scans, and is enabled by default. In both cases the portrait is displayed with an anonymizing bar placed across the eyes, and no handle, name, or account identifier is attached to it. You can disable portrait sharing at any time in your account settings, which removes your scans from other members' decks. You can request removal of a Glowup by contacting support@mogged.app.",
    },
    {
      heading: "Retention and deletion",
      body: "Your portraits and scan records are retained while your account is active to power your Vault, Routine, and Glowup history. Deleting your account from account settings deletes them: your portraits and the images derived from them, your scans and scores, your routine, your Glowups, and any feedback other members gave you. Your portraits are withdrawn from other members' decks immediately, and an active membership is cancelled as part of the deletion. Two things are kept, and only these two. Feedback you gave on other members' portraits is retained with your identity removed, because it has already been counted into results shown to those members. A minimal billing record — your payment-processor identifiers and the dates — is retained so payment disputes can be resolved and to meet tax and accounting obligations. Our payment processor also keeps its own transaction records, as the merchant of record, and we cannot delete those on your behalf.",
    },
    {
      heading: "Security",
      body: "Portraits are stored in private storage accessible only through your authenticated account, with processed images served through expiring links. We apply administrative and technical safeguards appropriate to the data we hold, though no method of transmission or storage is completely secure.",
    },
    {
      heading: "Your choices",
      body: "You may delete your account at any time from account settings, which erases your data as described under Retention and deletion. You may unsubscribe from reminder emails using the link in any reminder, turn portrait sharing off in account settings, and request access to or correction of your personal information by contacting support@mogged.app. Depending on where you live, you may have additional rights under local law.",
    },
    {
      heading: "Age policy",
      body: "The Service is not directed to anyone under 16. We require an age attestation at sign-up and apply automated age-estimation screening to submitted portraits. If we learn that a user is under 16, we will close the account and delete its data.",
    },
    {
      heading: "Changes to this policy",
      body: "We may update this Privacy Policy from time to time. Material changes will be communicated in-app, and the date above reflects the latest revision.",
    },
    {
      heading: "Contact",
      body: "Questions about this policy or your data can be directed to support@mogged.app.",
    },
  ],

  // First-run tutorial — interactive overlay shown once after onboarding.
  // Short, scan-first. Chrome (eyebrow, buttons) stays ALL-CAPS editorial;
  // headlines/body are sentence-case serif per the design (DESIGN.md §8).
  TUTORIAL_EYEBROW: "WELCOME TO MOGGED",
  TUTORIAL_SKIP_CTA: "SKIP",
  TUTORIAL_BACK_CTA: "BACK",
  TUTORIAL_NEXT_CTA: "NEXT",

  // Step 1 — the scan (the priority).
  TUTORIAL_SCAN_HEADLINE: "Start with a scan",
  TUTORIAL_SCAN_BODY:
    "Submit a portrait and the Oracle reads your face across 32 vectors: your score, your strengths, and three ways to refine. This is where you begin.",

  // Step 2 — passing references to the rest of the studio. The nav hint adapts
  // to where the navigation actually sits (desktop sidebar vs mobile tab bar).
  TUTORIAL_SECTIONS_HEADLINE: "Then explore the studio",
  TUTORIAL_SECTIONS_NAV_HINT_DESKTOP: "Everything lives in the sidebar on the left.",
  TUTORIAL_SECTIONS_NAV_HINT_MOBILE: "Everything lives in the bar along the bottom.",
  TUTORIAL_SECTION_VAULT_LABEL: "VAULT",
  TUTORIAL_SECTION_VAULT_DESC: "Your private archive of every scan.",
  TUTORIAL_SECTION_ROUTINE_LABEL: "ROUTINE",
  TUTORIAL_SECTION_ROUTINE_DESC: "The plan the Oracle builds from your results.",
  TUTORIAL_SECTION_EXPLORE_LABEL: "EXPLORE",
  TUTORIAL_SECTION_EXPLORE_DESC: "Encourage other members' glowups, anonymously.",

  // Step 3 — launch into the first scan.
  TUTORIAL_LAUNCH_HEADLINE: "Your first scan awaits",
  TUTORIAL_LAUNCH_BODY:
    "It takes only a moment. Submit a portrait to see where you stand.",
  TUTORIAL_LAUNCH_CTA: "SUBMIT YOUR PORTRAIT",

  // Glowup — vector display labels (mirrors VECTOR_DISPLAY_LABELS from oracleApi)
  // Used in judge chip row + peer-score readout headers.
  GLOWUP_VECTOR_SKIN: "SKIN",
  GLOWUP_VECTOR_JAW: "JAW",
  GLOWUP_VECTOR_BROWS: "BROWS",
  GLOWUP_VECTOR_EYES: "EYES",
  GLOWUP_VECTOR_HAIR: "HAIR",
  GLOWUP_VECTOR_LIPS: "LIPS",
  GLOWUP_VECTOR_HARMONY: "HARMONY",
  GLOWUP_VECTOR_SKIN_TONE: "SKIN TONE",

  // Public content pages (nav, About, FAQ, Contact, routed legal).
  // Crawlable marketing surfaces shown to signed-out visitors. Chrome stays
  // ALL-CAPS editorial; body copy is sentence-case serif per DESIGN.md §8.
  PUBLIC_NAV_ABOUT: "ABOUT",
  PUBLIC_NAV_FAQ: "FAQ",
  PUBLIC_NAV_LEGAL: "LEGAL",
  PUBLIC_NAV_CONTACT: "CONTACT",
  PUBLIC_NAV_SIGN_IN: "SIGN IN",
  PUBLIC_NAV_OPEN_APP: "OPEN APP",
  PAGE_TITLE_ABOUT: "About | MOGGED",
  PAGE_TITLE_FAQ: "FAQ | MOGGED",
  PAGE_TITLE_TERMS: "Terms of Service | MOGGED",
  PAGE_TITLE_PRIVACY: "Privacy Policy | MOGGED",
  PAGE_TITLE_CONTACT: "Contact | MOGGED",

  // About / How It Works
  ABOUT_EYEBROW: "ABOUT MOGGED",
  ABOUT_HEADLINE: "The Oracle, explained",
  ABOUT_INTRO_1:
    "MOGGED is a private studio for measured self-improvement. Submit a portrait and an AI analysis returns a scored reading of your face: where you stand, what is genuinely improvable, and a personalized routine for moving it.",
  ABOUT_INTRO_2:
    "Every scan is archived to your private Vault. Nothing is shown to anyone unless you choose to share it, and even then your identity stays behind the bar.",
  ABOUT_PROCESS_HEADLINE: "THE PROCESS",
  ABOUT_STEP_1_HEADLINE: "SUBMIT A PORTRAIT",
  ABOUT_STEP_1_BODY:
    "One still image: JPEG or PNG, front-facing, even lighting. The portrait is private to your account from the moment it arrives.",
  ABOUT_STEP_2_HEADLINE: "SCREENING",
  ABOUT_STEP_2_BODY:
    "Before any scoring, automated systems run age-estimation and content-safety checks. Portraits that fail screening are rejected and the uploaded image is deleted.",
  ABOUT_STEP_3_HEADLINE: "THE VERDICT",
  ABOUT_STEP_3_BODY:
    "The analysis returns an overall score from 1 to 100, built from seven weighted categories, each rolled up from individual trait reads. Each trait is scored and classed by whether a plan can actually move it.",
  ABOUT_STEP_4_HEADLINE: "THE ROUTINE",
  ABOUT_STEP_4_BODY:
    "From the traits with the most headroom, the Oracle assembles a standing AM and PM routine of products and habits matched to what your scan shows, refreshed with every new scan.",
  ABOUT_CATEGORIES_HEADLINE: "SEVEN CATEGORIES",
  ABOUT_CATEGORIES_INTRO:
    "The overall score is a weighted rollup of seven categories, each read from its own set of individual traits. The weights reflect how much each category carries in how a face reads.",
  // Keyed by taxonomy category key (contracts/taxonomy.js CATEGORIES). Tests
  // assert an entry exists for every category — a taxonomy change fails loudly.
  ABOUT_CATEGORY_DESCRIPTIONS: {
    skin_quality:
      "Clarity, texture, tone, hydration. The heaviest category, and the most responsive to a consistent routine.",
    hair_scalp:
      "Hairline, density, condition, and cut. Slow to move, but it moves.",
    facial_hair:
      "Shaping, density, and condition. Read only where facial hair is present.",
    brows_lashes:
      "Brow shape and density, lash density. Small area, outsized framing effect.",
    mouth_smile:
      "Teeth, gums, and lips. A mix of fast wins and longer projects.",
    definition:
      "Facial leanness, puffiness, and posture. Driven almost entirely by habits.",
    bone_structure:
      "Jaw, chin, cheekbones, symmetry. Scored for honesty: never coached, never sold against.",
  },
  ABOUT_MODIFIABILITY_HEADLINE: "WHAT CAN ACTUALLY CHANGE",
  ABOUT_MODIFIABILITY_BODY:
    "Every trait carries a class: modifiable, partial, or fixed. Modifiable traits respond to products and habits. Partial traits improve to a genetic ceiling, and the Oracle says so. Fixed traits such as bone structure are scored for an honest reading but never coached and never used to sell you anything. The routine is built only from what can move.",
  ABOUT_DISCLAIMER:
    "MOGGED offers general cosmetic guidance, not medical advice or diagnosis. Consult a qualified professional before acting on any recommendation.",
  ABOUT_CTA: "SUBMIT PORTRAIT FOR ANALYSIS",

  // FAQ
  FAQ_EYEBROW: "FREQUENTLY ASKED",
  FAQ_HEADLINE: "Questions, answered",
  FAQ_ITEMS: [
    {
      question: "What is MOGGED?",
      answer:
        "MOGGED is a private AI face-analysis service. You submit a portrait; automated analysis returns an overall score, seven scored categories, and a personalized routine of products and habits aimed at what your scan shows is improvable.",
    },
    {
      question: "How does the scoring work?",
      answer:
        "Each portrait is read across dozens of individual traits, which roll up into seven weighted categories: skin quality, hair, facial hair, brows and lashes, mouth and smile, facial definition, and bone structure. The category scores combine into an overall score from 1 to 100.",
    },
    // ⚠ DESCRIBES OPT-OUT. Four surfaces state this same behaviour and they
    // must not drift — drifting one is exactly how the last contradiction
    // happened, and it survived on a crawlable page for months:
    //
    //   this answer
    //   PRIVACY_SECTIONS  "What other members see"
    //   TOS_SECTIONS      "Sharing and community features"
    //   COPY.SHARING_BODY the Account toggle, read at the point of decision
    //
    // Two claims here are load-bearing and both are verifiable in code. That
    // sharing defaults ON comes from SCAN_SHARING_DEFAULT_ON via
    // utilities/scanSharing.js resolveJudgable(). That turning it off withdraws
    // PAST scans comes from setScanSharing.js, which fans the preference out
    // across every existing scan doc and runs the off-direction first. If
    // either behaviour changes, this copy is a false promise the same day.
    //
    // WRITTEN TIMELESS, ON PURPOSE. "Your scans" is unqualified, so it covers
    // every scan the account holds — including ones migration/
    // backfillScanJudgable.js enrolled retroactively. Do not add "including
    // scans you took before" or "before this setting existed": a reader has no
    // idea there was a previous setting, and the phrasing invents a distinction
    // the product does not have.
    //
    // A legal page states what is true now. Announcing a CHANGE is a different
    // job with a different channel — the notification send (migration/
    // sendRebrandBlast.js). Do not try to do the second job here; it makes this
    // answer worse without making the notice reach anyone.
    {
      question: "Are my photos public?",
      answer:
        "Nothing is public, and your scores and Vault stay private to your account. Two things can show a portrait to another signed-in member. Submitting a Glowup shares a before-and-after pair, and happens only if you submit one. Portrait sharing lets other members give structured feedback on your scans, and it is on by default. In both cases the portrait appears with an anonymizing bar across the eyes, and no handle, name, or account identifier is attached to it. You can turn portrait sharing off at any time in your account settings, which removes your scans from other members' decks.",
    },
    {
      question: "What happens to a portrait after I submit it?",
      answer:
        "It is transmitted to the third-party AI services that perform face detection, age estimation, aesthetic analysis, and content-safety screening on our behalf. Portraits that fail screening are rejected and the uploaded image is deleted. Accepted scans are stored privately to power your Vault and routine.",
    },
    {
      question: "Is there an age requirement?",
      answer:
        "Yes. MOGGED is reserved for visitors aged 16 and older. We require an age attestation at sign-up and apply automated age-estimation screening to every submitted portrait.",
    },
    // Rewritten 2026-08-05: the previous answer described the offerwall
    // ("scan credits earned by completing offers"), which no longer exists.
    // Rewritten again when the daily cap came off — it no longer claims
    // "one scan per day", and it no longer claims "membership is free", which
    // was the second of the two live contradictions CLAUDE.md flags.
    //
    // ⚠ STILL INCOMPLETE FOR ENTITLEMENTS_ENFORCED. "Scanning is free" stays
    // true under the paywall, but this answer does not yet say that a paid
    // membership exists or what it costs, and the same page carries AdSense
    // publisher content — worth checking whether a paid tier alongside AdSense
    // on the same surfaces raises a policy question.
    {
      question: "Does it cost anything?",
      answer:
        "No. Scanning is free, and there is no daily limit on how many you take.",
    },
    {
      question: "Can my score change over time?",
      answer:
        "That is the point. The routine targets your most improvable traits, and each new scan re-reads them. The Vault keeps every scan so you can compare readings across months.",
    },
    {
      question: "Is bone structure really scored?",
      answer:
        "Yes, for honesty. Fixed structural traits are scored so the overall reading is real, but they are never coached and never used to recommend a product. The routine is built only from traits that can actually move.",
    },
    {
      question: "Is this medical advice?",
      answer:
        "No. MOGGED offers general cosmetic guidance only. Nothing in the service is medical advice, diagnosis, or treatment, and recommendations involving prescription-strength products direct you to a clinician rather than a product.",
    },
    {
      question: "How do I delete my account or my data?",
      answer:
        "Account settings, any time. It deletes your portraits and the images made from them, your scans and scores, your routine, your Glowups, and the feedback other members gave you. Your portraits leave other members' decks straight away, and an active membership is cancelled as part of it. Two things are kept: feedback you gave on other people's portraits, with your identity stripped off it, because it has already been counted into what those members were shown; and a minimal billing record for payment disputes and tax. Deletion cannot be undone.",
    },
    {
      question: "How does MOGGED make money?",
      answer:
        "The service displays third-party advertising, and product recommendations use affiliate links, so we may earn a commission on qualifying purchases. Recommendations are selected by the analysis, and fixed traits are never sold against.",
    },
    {
      question: "How do I reach support?",
      answer:
        "Email support@mogged.app for account questions, data requests, Glowup removal, or anything else. The Contact page has the details.",
    },
  ],

  // Contact
  CONTACT_EYEBROW: "CONTACT",
  CONTACT_HEADLINE: "Reach the team",
  CONTACT_BODY:
    "Account questions, data access or deletion requests, Glowup removal, press, or anything unresolved: a single inbox reaches the team.",
  CONTACT_EMAIL: "support@mogged.app",
  CONTACT_NOTE:
    "Include the email address on your account so we can locate your records. Expect a reply within a few business days.",

  // Routed legal pages (/legal/terms, /legal/privacy)
  LEGAL_PAGE_EYEBROW: "LEGAL",

  // ── Membership (Stripe) ───────────────────────────────────────────────────
  // Register: editorial museum text. The paywall is a gallery label, not a
  // sales page — one statement, one price, one action. No urgency copy, no
  // countdown, no "unlock now".
  MEMBERSHIP_EYEBROW: "MEMBERSHIP",
  // The eyebrow on every full-size paywall card. Replaced the ACCESS
  // RESTRICTED Censor Bar: leading with who is selling beats leading with the
  // denial, and the bar framed a finished product as a locked door.
  MEMBERSHIP_EYEBROW_PRO: "MOGGED PRO",
  MEMBERSHIP_HEADLINE: "The complete reading",
  MEMBERSHIP_PRICE: "$4.99 / MONTH",
  MEMBERSHIP_TRIAL_NOTE:
    "Begins with a free trial. Cancel before it ends and nothing is charged.",

  // Merchant-of-record disclosure. Deliberately plain rather than editorial:
  // under Managed Payments the charge reads LINK.COM* on the customer's
  // statement and receipts arrive from Link, not from MOGGED. A statement line
  // the customer does not recognise is the single largest driver of avoidable
  // chargebacks, and chargebacks threaten Managed Payments eligibility itself.
  // This is the one place museum-text would cost real money.
  MEMBERSHIP_MOR_NOTE:
    "Payments are handled by Link, Stripe's payment service, which acts as the merchant for your purchase. Receipts come from Link, and your statement shows LINK.COM · MOGGED MEMBERSHIP.",
  MEMBERSHIP_BENEFITS: [
    "Every analysis opens the moment it completes.",
    "The full regimen, with strength progression.",
    "Imagine My Glowup — a rendering of the regimen followed.",
    "The archive, comparisons, and the weekly dispatch.",
  ],

  // The paywall body. `reason` selects the line; the fallback carries the
  // surface-agnostic statement.
  MEMBERSHIP_PAYWALL: "This surface is held for members.",
  MEMBERSHIP_PAYWALL_REASONS: {
    routine: "The regimen is held for members.",
    scan_unlock: "This analysis is held for members.",
    glowup_render: "Rendering is held for members.",
    vault: "The archive is held for members.",
    // NOT "Glowups are held for members" — making one is free, and has been
    // since creation was ungated (CLAUDE.md rule 5). Only hearing back from
    // peers is paid, and getGlowupPeerScore is the only callable this line can
    // now be shown for.
    glowup: "Peer feedback is held for members.",
    newsletter: "The weekly dispatch is held for members.",
  },

  MEMBERSHIP_CTA: "BEGIN FREE TRIAL",
  MEMBERSHIP_CTA_NO_TRIAL: "PROCEED TO MEMBERSHIP",
  // ── Cancelling ────────────────────────────────────────────────────────────
  //
  // Register shift, on purpose. Everywhere else the product is museum-text and
  // a little cold. Someone leaving does not want to be curated at — they want
  // plain sentences and no friction. This is the one flow where the brand voice
  // steps back.
  //
  // Never apologetic, never guilt-tripping ("are you sure you want to give up
  // your glowup?"). Say what happens, then get out of the way.
  CANCEL_MODAL_EYEBROW: "CANCEL MEMBERSHIP",
  CANCEL_MODAL_HEADLINE: "Before you go",
  CANCEL_MODAL_BODY: "Your membership will end at the close of the current period. Nothing is deleted.",
  CANCEL_MODAL_REASON_TITLE: "WHAT PROMPTED THIS?",
  CANCEL_MODAL_NOTE_LABEL: "ANYTHING ELSE? (OPTIONAL)",
  CANCEL_MODAL_NOTE_PLACEHOLDER: "Tell us what would have kept you.",
  CANCEL_MODAL_KEEPS_ACCESS_PREFIX: "You keep full access until",
  CANCEL_MODAL_KEEPS_DATA: "Your scans, readings and routine stay in your account.",
  CANCEL_MODAL_KEEP_CTA: "KEEP MY MEMBERSHIP",
  // Plain and unambiguous. "Confirm" alone leaves people unsure what they are
  // confirming at the exact moment that matters.
  CANCEL_MODAL_CONFIRM_CTA: "CANCEL MEMBERSHIP",
  CANCEL_MODAL_ERROR: "The membership could not be cancelled. Try again, or use Manage membership.",
  // Status page: the entry point, and the state after it is scheduled.
  MEMBERSHIP_CANCEL_CTA: "CANCEL MEMBERSHIP",
  MEMBERSHIP_CANCEL_SCHEDULED: "Cancellation scheduled. You keep full access until then.",
  MEMBERSHIP_MANAGE_CTA: "MANAGE MEMBERSHIP",
  // Account's entry point when there is no subscription behind the status.
  // "MANAGE MEMBERSHIP" would promise a thing to manage; through that door is
  // the offer, so the label names the action instead. Deliberately NOT "BEGIN
  // FREE TRIAL" — it routes to /membership, where the price, the trial terms
  // and the merchant-of-record disclosure live, rather than straight to
  // Checkout. A button that says "free trial" and opens a card form has
  // skipped the page that explains what is being agreed to.
  MEMBERSHIP_CARD_UPGRADE_CTA: "UPGRADE NOW",
  MEMBERSHIP_UPDATE_PAYMENT_CTA: "UPDATE PAYMENT",
  MEMBERSHIP_RETURN_CTA: "RETURN",

  // ── Account membership card ───────────────────────────────────────────────
  //
  // The card splits what the old status chip said in one breath into two
  // registers: the EYEBROW is the tier you are on (museum text, caps), the
  // STANDING is whether that tier is currently in good order (caps, quieter).
  // "MEMBER · ACTIVE" was both at once, which is why the card cannot reuse it
  // — printing the eyebrow and the standing from the same string would say
  // ACTIVE twice.
  //
  // The headline and detail are sentence case. That is not the SummaryBlock
  // exception (CLAUDE.md) creeping outward — the membership surface has always
  // written its headlines this way ("The complete reading", "Membership
  // confirmed", "Nothing was charged"), and this card is that surface.
  MEMBERSHIP_CARD_TIER_MEMBER: "MEMBER",
  MEMBERSHIP_CARD_STANDING_ACTIVE: "ACTIVE",
  MEMBERSHIP_CARD_STANDING_ENDING: "ENDING",
  MEMBERSHIP_CARD_STANDING_UNRESOLVED: "UNRESOLVED",

  // Composed in the component as `${n} ${suffix}`, the same idiom TrialBadge
  // uses. Day zero is its own line rather than "0 days left", which reads as
  // though access has already gone when it has not.
  MEMBERSHIP_CARD_TRIAL_SUFFIX_ONE: "day left in trial",
  MEMBERSHIP_CARD_TRIAL_SUFFIX_MANY: "days left in trial",
  MEMBERSHIP_CARD_TRIAL_LAST_DAY: "Last day of your trial",
  MEMBERSHIP_CARD_TRIAL_HEADLINE: "Trial active",
  MEMBERSHIP_CARD_ACTIVE_HEADLINE: "Membership active",
  MEMBERSHIP_CARD_PAST_DUE_HEADLINE: "Payment unresolved",
  MEMBERSHIP_CARD_ENDED_HEADLINE: "Membership ended",
  MEMBERSHIP_CARD_FREE_HEADLINE: "Free account",
  MEMBERSHIP_CARD_UNAVAILABLE_HEADLINE: "Membership unavailable",

  MEMBERSHIP_CARD_PAST_DUE_DETAIL: "Access continues while the card is retried.",
  MEMBERSHIP_CARD_FREE_DETAIL:
    "Membership opens the full reading, the regimen, and the archive.",
  MEMBERSHIP_CARD_UNAVAILABLE_DETAIL:
    "Your membership status could not be read just now. Nothing has changed.",

  // ── /membership status page ───────────────────────────────────────────────
  //
  // The billing table. Account's card is the summary and the door; every
  // number lives here, once, where there is room to label it.
  //
  // Sentence-case price, unlike MEMBERSHIP_PRICE ("$4.99 / MONTH") — that one
  // is a display figure on the paywall panel, this one is a table value beside
  // a small-caps label and would shout next to it.
  // Home's peer readout, blurred for a free account. Names what is behind the
  // blur rather than that it is locked — the shape is already visible, so
  // "held for members" would be describing something they can see.
  FEEDBACK_LOCKED_BODY: "See which traits other members named, and how many named each.",
  FEEDBACK_LOCKED_CTA: "GO PRO",

  MEMBERSHIP_BACK_LABEL: "Back to Account",
  MEMBERSHIP_BILLING_EYEBROW: "BILLING",
  MEMBERSHIP_INCLUDED_EYEBROW: "INCLUDED",
  MEMBERSHIP_PLAN_NAME: "MOGGED Pro · Monthly",
  MEMBERSHIP_PRICE_MONTHLY: "$4.99 / month",
  MEMBERSHIP_ROW_PLAN: "PLAN",
  MEMBERSHIP_ROW_PRICE: "PRICE",
  MEMBERSHIP_ROW_TRIAL_ENDS: "TRIAL ENDS",
  MEMBERSHIP_ROW_FIRST_CHARGE: "FIRST CHARGE",
  MEMBERSHIP_ROW_RENEWS: "RENEWS",
  MEMBERSHIP_ROW_ACCESS_ENDS: "ACCESS ENDS",
  MEMBERSHIP_ROW_MEMBER_SINCE: "MEMBER SINCE",
  // Shown in place of FIRST CHARGE once cancellation is scheduled. Leaving the
  // row out entirely would be quieter, but "no further charge" is the single
  // thing someone who just cancelled came back to confirm.
  MEMBERSHIP_NO_FURTHER_CHARGE: "None",

  MEMBERSHIP_STATUS_ACTIVE: "MEMBER · ACTIVE",
  MEMBERSHIP_STATUS_TRIAL: "MEMBER · TRIAL",
  MEMBERSHIP_STATUS_PAST_DUE: "PAYMENT UNRESOLVED",
  MEMBERSHIP_STATUS_CANCELED: "MEMBERSHIP ENDED",
  // The free tier's own name. Account used to badge EVERY user "MOGGED MEMBER"
  // in Aurum, which told people who had never paid that they were members.
  // Neutral wording and neutral chrome — Aurum is allow-listed to active
  // states (DESIGN.md §2.1), and a free account is not one.
  MEMBERSHIP_STATUS_FREE: "FREE ACCOUNT",

  // Rendered as "TRIAL · 5 DAYS REMAINING". Singular handled in the component.
  MEMBERSHIP_TRIAL_REMAINING_ONE: "DAY REMAINING",
  MEMBERSHIP_TRIAL_REMAINING_MANY: "DAYS REMAINING",
  MEMBERSHIP_RENEWS_PREFIX: "Renews",
  MEMBERSHIP_ENDS_PREFIX: "Access ends",
  // NOT MEMBERSHIP_ENDS_PREFIX. On trial_end a healthy trial converts and
  // billing starts — access does not stop. "Access ends Tuesday" to someone
  // who is about to become a paying member is wrong in the direction that
  // causes cancellations and support mail.
  MEMBERSHIP_TRIAL_ENDS_PREFIX: "Trial ends",

  MEMBERSHIP_PAST_DUE_BODY:
    "The last payment did not clear. Access continues while the card is retried. Updating the payment method resolves it immediately.",

  // /membership/success — reads the live entitlement doc, never the query
  // string, because the webhook may not have landed when Stripe redirects.
  MEMBERSHIP_SUCCESS_HEADLINE: "You're in",
  MEMBERSHIP_SUCCESS_BODY:
    "You have full access. Your routine, your renders, and the archive are waiting.",
  MEMBERSHIP_PROCESSING_HEADLINE: "Confirming",
  MEMBERSHIP_PROCESSING_BODY:
    "The payment is settling. This resolves on its own within a moment.",
  MEMBERSHIP_PROCESSING_SLOW:
    "This is taking longer than usual. The membership is safe; refresh shortly, or contact support if it persists.",
  MEMBERSHIP_CANCEL_HEADLINE: "Nothing was charged",
  MEMBERSHIP_CANCEL_BODY:
    "The checkout was not completed. The free path remains open.",

  MEMBERSHIP_ERROR: "Membership is temporarily unavailable. Try again shortly.",

  // ── Imagine My Glowup ─────────────────────────────────────────────────────
  // Every string here has to stay honest: the output is a SIMULATION, not a
  // prediction and not a cosmetic-outcome claim.
  GLOWUP_RENDER_CTA: "IMAGINE MY GLOWUP",
  // No GLOWUP_RENDER_BAR. The [SIMULATED] Censor Bars are gone from the modal
  // — the mark that does the work is burned into the stored JPEG by
  // renderFunctions/watermark (it has to survive a download), and
  // GLOWUP_RENDER_DISCLAIMER states it in words. See GlowupRenderModal.
  GLOWUP_RENDER_HEADLINE: "Your routine, imagined",
  GLOWUP_RENDER_BODY:
    "An illustration of what consistent use might look like. Generated by AI from your last reading — not a prediction, and not a promise.",
  GLOWUP_RENDER_GENERATING: "GENERATING",
  GLOWUP_RENDER_BEFORE_LABEL: "TODAY",
  GLOWUP_RENDER_AFTER_LABEL: "IMAGINED",
  GLOWUP_RENDER_CLOSE_CTA: "CLOSE",
  // No GLOWUP_RENDER_REGENERATE_CTA: the cap is one render per day, so a
  // regenerate control would refuse every press. See GlowupRenderModal.
  GLOWUP_RENDER_SAVE_CTA: "SAVE",
  GLOWUP_RENDER_DRAG_HINT: "Drag the divider to compare.",
  // The projection panel. The number is the PUBLISHED adherence bonus from
  // ADHERENCE_META — the same delta the Routine page already promises per
  // active — so this states an existing promise rather than inventing a
  // forecast. Register stays conditional: "if", never "will".
  GLOWUP_RENDER_PROJECTION_LABEL: "IF YOU STAY CONSISTENT",
  GLOWUP_RENDER_PROJECTION_FROM: (today) => `projected from ${today} today`,
  GLOWUP_RENDER_PROJECTION_AT_CEILING:
    "You are already reporting consistent use across this regimen.",
  GLOWUP_RENDER_DRIVERS_LABEL: "WHAT'S DRIVING THIS",
  GLOWUP_RENDER_DISCLAIMER:
    "This image is generated, not photographed. It assumes consistent use and "
    + "can't account for genetics, health, or age. Structural features are left unchanged.",
  GLOWUP_RENDER_ERRORS: {
    RATE_LIMITED: "That is all the renderings for today. More tomorrow.",
    AGE_RESTRICTED: "Rendering is not available on this account.",
    NOTHING_TO_RENDER: "This reading has nothing a routine would change.",
    RENDER_REFUSED: "The rendering could not be completed. Try again shortly.",
  },
  GLOWUP_RENDER_ERROR_FALLBACK: "The rendering could not be completed. Try again shortly.",

  // ── Reported product use (scan flow) ──────────────────────────────────────
  // Asked before the scan runs, for members on a second or later reading.
  // No USAGE_STEP_BAR. The [BEFORE WE READ] Censor Bar is gone from the step
  // — it duplicated the portrait's status pill and cost the list a row.
  USAGE_STEP_HEADLINE: "Have you been using these?",
  USAGE_STEP_BODY:
    "Your answers adjust the reading and set the pace of your regimen. They are shown separately from the measured score.",
  USAGE_STEP_CONFIRM_CTA: "CONFIRM AND ANALYZE",
  USAGE_STEP_SKIP_CTA: "SKIP THIS",

  // ── Unlock gate ───────────────────────────────────────────────────────────
  // Shown when a completed analysis is withheld. Note the register: the
  // analysis is FINISHED and HELD, never "failed" and never "locked out". The
  // work is done; the door is what is closed.
  // No UNLOCK_GATE_BAR. The modal deliberately has no Censor Bar eyebrow —
  // naming the seal above the offer frames the moment as a denial. The dialog's
  // accessible name is UNLOCK_GATE_HEADLINE. UNLOCK_GATE_SEALED_STATUS below is
  // a different surface (the Create status chip) and still says SEALED.
  UNLOCK_GATE_HEADLINE: "Your analysis is ready",
  UNLOCK_GATE_BODY:
    "Open it now with Pro — or earn this one by giving feedback on ten scans in Explore.",
  // Routine surface. Deliberately does NOT offer the judging path: the routine
  // is membership-only on both paths (CLAUDE.md rule 2) and getRoutine has no
  // judging unlock behind it. Passed to UnlockGateModal as `headline` +
  // `subcopy` alongside showJudgePath={false}.
  //
  // The headline names the ROUTINE, not the analysis. On this surface the
  // analysis may well already be open — saying "your analysis is ready" over a
  // blurred regimen describes the wrong artifact and reads as a bug.
  UNLOCK_GATE_HEADLINE_ROUTINE: "Your routine is ready",
  UNLOCK_GATE_BODY_ROUTINE:
    "Your regimen is built and waiting. Membership opens it, along with every scan you take.",
  // NOT NOW on the routine leaves the blurred regimen on screen rather than
  // ejecting to /home. RoutinePaywallPanel is what stays there — a standing
  // offer, not a "reopen the modal" link, so its CTA goes to checkout and the
  // trial terms below it carry the real price.
  UNLOCK_GATE_ROUTINE_EYEBROW: "MOGGED PRO",
  UNLOCK_GATE_ROUTINE_CTA: "UNLOCK WITH PRO",
  // Shown once the debt is cleared but the payload has not been re-fetched.
  UNLOCK_GATE_BODY_OPEN:
    "This analysis is open. Give feedback again any time to unlock the next one.",
  UNLOCK_GATE_TRIAL_CTA: "BEGIN FREE TRIAL",
  UNLOCK_GATE_JUDGE_CTA: "GIVE FEEDBACK",
  UNLOCK_GATE_RESUME_CTA: "RESUME FEEDBACK",
  UNLOCK_GATE_DISMISS_CTA: "NOT NOW",

  // ── Pro path (the priced half of the gate) ────────────────────────────────
  //
  // The anchor price, the "50% OFF FIRST MONTH" badge and the "INTRO PRICE
  // HOLDS" countdown are GONE, and none of the three should come back without
  // a real offer behind them. The anchor was struck through a price this
  // product never charged (a fabricated reference price); the countdown was
  // per-mount, restarted on reopen, and enforced by nothing on the server, so
  // the price it claimed to hold could not actually expire. Both also broke
  // the rule the membership block above states outright: "one statement, one
  // price, one action. No urgency copy, no countdown, no 'unlock now'."
  //
  // PRICE: $4.99, which is what Stripe actually charges — the subscription
  // price is created at `unit_amount: 499` in
  // functions/migration/createMembershipProduct.js, and MEMBERSHIP_PRICE above
  // says the same. These three must never disagree again: a displayed price
  // that is not the charged price is a chargeback and a consumer-protection
  // problem, not a copy nit. The modal previously showed $11.99, which came
  // from the design and was never charged. priceAgreement.test.js binds them.
  //
  // Tax: the live Price carries no tax_behavior, and the Stripe Tax default
  // resolves to INCLUSIVE — every live invoice line to date reads
  // `tax_behavior: inclusive` — so $4.99 is the all-in figure and VAT is
  // carved out of it, not added on top. If that dashboard default is ever
  // flipped to exclusive, this copy becomes a false price for EU customers.
  UNLOCK_GATE_PRO_EYEBROW: "INSTANT ACCESS",
  UNLOCK_GATE_PRO_TITLE: "Unlock every scan",
  UNLOCK_GATE_PRO_FLAG: "MOST CHOSEN",
  UNLOCK_GATE_PRICE: "$4.99",
  UNLOCK_GATE_PRICE_PERIOD: "/mo",
  UNLOCK_GATE_PRO_BENEFITS: [
    "Every scan unlocked the moment it finishes",
    "Full 35-measure breakdown and observations",
    "Routine efficacy tracking between scans",
  ],
  UNLOCK_GATE_TRIAL_TERMS: "7 days free, then $4.99/mo. Cancel anytime.",

  // ── Judging path (the earned half) ────────────────────────────────────────
  UNLOCK_GATE_OR: "OR EARN IT",
  UNLOCK_GATE_EARN_EYEBROW: "FEEDBACK TO UNLOCK",
  UNLOCK_GATE_EARN_TITLE: "Give ten, get yours",
  UNLOCK_GATE_EARN_COUNT_LABEL: "FEEDBACK GIVEN",
  // Rendered as "Give feedback on <n more scans> in Explore and this analysis
  // opens free." Singular handled in the component.
  UNLOCK_GATE_EARN_REMAIN_ONE: "1 more scan",
  UNLOCK_GATE_EARN_REMAIN_MANY: "more scans",
  UNLOCK_GATE_EARN_PREFIX: "Give feedback on",
  UNLOCK_GATE_EARN_SUFFIX: "in Explore and this analysis opens free.",

  // ── Cleared state ─────────────────────────────────────────────────────────
  UNLOCK_GATE_CLEARED_EYEBROW: "ANALYSIS UNLOCKED",
  UNLOCK_GATE_CLEARED_HEADLINE: "Ten given. Yours is open.",
  UNLOCK_GATE_CLEARED_BODY: "Thanks for encouraging the community.",
  UNLOCK_GATE_CLEARED_CTA: "VIEW MY RESULTS",
  UNLOCK_GATE_SEALED_STATUS: "ANALYSIS SEALED · TEN SCANS TO OPEN",
  // ── One reading outstanding at a time ─────────────────────────────────────
  //
  // Refused BEFORE the portrait uploads, so the copy has to say why the press
  // did nothing and where to go — a bare "declined" reads as a content
  // rejection, which this is not. Names the previous reading, not this one.
  //
  // Deliberately does NOT say "daily limit": scans remain unlimited. The
  // constraint is one OPEN reading at a time, and conflating the two would
  // send the user away to wait for a reset that never comes.
  // Only these two: UnlockGateModal takes `headline` and `subcopy` overrides
  // and nothing else, so an eyebrow or CTA key here would be copy nothing can
  // render.
  SCAN_PENDING_UNLOCK_STATUS: "LAST READING STILL SEALED · OPEN IT TO CONTINUE",
  SCAN_PENDING_UNLOCK_HEADLINE: "Your last reading is still sealed.",
  SCAN_PENDING_UNLOCK_BODY:
    "The Oracle holds one reading open at a time. Give feedback on ten scans to unseal it, or begin a trial to open everything.",
  // Vault tile + detail. A locked row keeps its thumbnail, so without this the
  // masked score reads as a missing score rather than a withheld one.
  //
  // The Vault says LOCKED, not SEALED. "Sealed" is still the domain term in
  // code and on the Create status chip (UNLOCK_GATE_SEALED_STATUS above), but
  // the Vault is a browsing surface where a one-word tile badge has to be
  // immediately obvious, and a padlock is a thing everyone already knows.
  // The tile also draws a progress bar from the row's `unlock` block, so the
  // a11y label reports the same count a sighted user sees on it.
  VAULT_TILE_LOCKED: "LOCKED",
  // Keeps the "how to open it" clause. A sighted user infers the way out from
  // the pips and the gate they land on; a screen-reader user gets one string,
  // so dropping it would leave them told the thing is shut and nothing else.
  VAULT_TILE_LOCKED_A11Y: "locked analysis — open it by giving feedback on ten scans or by starting a membership",
  // Rendered as "3 of 10 responses given". The tile shows the bar; this is
  // what a screen reader gets instead. A countable noun, not "feedback given" —
  // feedback is a mass noun and "3 of 10 feedback" does not parse.
  VAULT_TILE_LOCKED_PROGRESS_A11Y: "responses given",
  // Rendered under the bar as "7 TO OPEN" / "1 TO OPEN".
  VAULT_TILE_LOCKED_REMAINING: "TO OPEN",
  UNLOCK_GATE_VAULT_NOTE: "Locked. Open this analysis to view it in full.",

  // ── Scan judging ──────────────────────────────────────────────────────────
  // The judging surface is anonymous in both directions: the judge never
  // learns whose scan it is, and the owner never learns who judged.
  SCAN_JUDGE_EYEBROW: "EXPLORE",
  // Explore calls the thing being judged a SCAN, not a portrait. "Portrait" is
  // still the right word on the capture side — you submit a portrait — but by
  // the time it reaches Explore it is a completed scan, and that is also what
  // the person judging is deciding about.
  //
  // These two label the mode switch on both Explore surfaces. They were
  // hardcoded in ScanJudge.js and GlowupJudge.js, so the same tab strip was
  // spelled twice and had to be renamed in two files.
  SCAN_JUDGE_TAB: "SCANS",
  GLOWUP_JUDGE_TAB: "GLOWUPS",
  SCAN_JUDGE_HEADLINE: "Give feedback on this scan",
  SCAN_JUDGE_STRENGTHS_PROMPT: "STRONGEST FEATURES",
  SCAN_JUDGE_IMPROVEMENTS_PROMPT: "AREAS TO WORK ON",
  SCAN_JUDGE_PICK_HINT: "Select two to four in each.",
  // ── The hidden overall rating ─────────────────────────────────────────────
  //
  // SCAN_JUDGE_SCORE_HINT ("Private. Never shown to them.") used to sit under
  // this and was removed on request. It carried real weight: told nothing, a
  // rater may assume the number lands in front of the face they are looking at,
  // rate defensively upward, and compress the distribution into uselessness.
  // See ScorePicker.js — if scores start bunching at 7-8, put it back.
  //
  // "OVERALL" not "SCORE": the verdict's score is the model's, on a different
  // scale, and the two must not read as the same number.
  // ── The persistent membership offer (ProBanner) ───────────────────────────
  //
  // Sits on a gold plate at the top of every scrolling surface, so it competes
  // with the page it sits above. It earns that by being short.
  //
  // NAMES THE TIER, not the effect. This used to read "UNSEAL EVERYTHING" on the
  // argument that what the membership DOES beats what it is called. Three words
  // of museum-voice on a gold bar turned out to read as decoration rather than a
  // control — "Get Pro" is legible as a button at a glance, which is the entire
  // job of a bar the reader is trying to scroll past.
  //
  // Sentence case, deliberately: it renders in the serif rather than the Censor
  // Bar face (see ProBanner), and caps + a serif + an underline is three
  // emphases fighting each other.
  //
  // Does not name a price. The bar is an invitation to the offer, not the offer
  // — the price lives on the checkout it opens, and a number here would need
  // the VAT qualifier that UNLOCK_GATE_PRICE already carries.
  PRO_BANNER_HEADLINE: "Get Pro",
  PRO_BANNER_SUBTITLE: "Every reading, every routine, no feedback required.",
  PRO_BANNER_DISMISS_LABEL: "Dismiss membership offer",
  SCAN_JUDGE_SCORE_TITLE: "OVERALL",
  SCAN_JUDGE_SUBMIT_CTA: "SUBMIT FEEDBACK",
  // Names all three requirements now that a rating is one of them — the old
  // copy said "two selections in each column" while the button stayed disabled
  // for a missing rating, which reads as a broken button.
  SCAN_JUDGE_SUBMIT_DISABLED_HINT: "Two selections in each column, plus an overall rating.",
  SCAN_JUDGE_SKIP_CTA: "SKIP THIS SCAN",
  // Rendered as "FEEDBACK GIVEN 03 / 10" — a counter, so the past participle
  // reads better than the bare noun, and it matches
  // UNLOCK_GATE_EARN_COUNT_LABEL on the modal.
  SCAN_JUDGE_PROGRESS_PREFIX: "FEEDBACK GIVEN",
  SCAN_JUDGE_ANONYMOUS_NOTE:
    "Feedback is anonymous in both directions. Nothing you select is attributed to you.",
  SCAN_JUDGE_TOO_FAST:
    "Take a moment with the scan. This feedback was submitted too quickly.",
  SCAN_JUDGE_LOW_EFFORT:
    "The last few responses were identical. The deck has been refreshed.",
  SCAN_JUDGE_RATE_LIMITED:
    "That is a great deal of feedback for one hour. Continue shortly.",
  SCAN_JUDGE_ERROR: "That feedback could not be recorded. Try the next scan.",
  SCAN_JUDGE_EMPTY_HEADLINE: "No new scans",
  // TWO BODIES, because an empty deck means two different things.
  //
  // ..._BODY is for the one person the membership line actually helps: a free
  // member with a SEALED reading, who cannot open it by giving feedback today
  // because there is nothing left to give feedback on.
  //
  // ..._BODY_OPEN is for everyone else — members, and free users with nothing
  // sealed. Explore is open to all of them, so an empty deck is just an empty
  // deck, and a membership pitch here read as though browsing required one.
  SCAN_JUDGE_EMPTY_BODY:
    "No scans are awaiting feedback. Membership opens your analysis without one.",
  SCAN_JUDGE_EMPTY_BODY_OPEN:
    "Nothing is awaiting feedback right now. New portraits arrive through the day.",
  SCAN_JUDGE_COMPLETE_HEADLINE: "Feedback given on ten scans",
  SCAN_JUDGE_COMPLETE_BODY: "Your analysis is open.",
  SCAN_JUDGE_COMPLETE_CTA: "VIEW YOUR ANALYSIS",

  // ── Peer feedback (Home + Vault) ──────────────────────────────────────────
  // DESIGN.md exception, documented in DESIGN.md itself: this is the one
  // surface that shows non-positive peer signal. Scoped to AGGREGATE counts,
  // on the owner's OWN scan only, never an individual verdict, never red.
  FEEDBACK_SECTION_LABEL: "WHAT OTHERS SAW",
  FEEDBACK_STRENGTHS_LABEL: "STRONGEST",
  FEEDBACK_IMPROVEMENTS_LABEL: "TO WORK ON",
  FEEDBACK_COUNT_ONE: "response",
  FEEDBACK_COUNT_MANY: "responses",
  FEEDBACK_EMPTY_BODY: "No feedback yet. It arrives as members work through Explore.",
  // The Vault tab. "GIVEN" only parsed next to the word it was short for; on
  // its own in a tab strip beside SCANS and GLOWUPS it reads as a fragment.
  FEEDBACK_GIVEN_TAB: "FEEDBACK",
  FEEDBACK_GIVEN_EMPTY_HEADLINE: "No feedback given",
  FEEDBACK_GIVEN_EMPTY_BODY:
    "Feedback you give is recorded here. The scans stay anonymous.",

  // ── The FEEDBACK tab's two directions ─────────────────────────────────────
  // A SegmentedSwitch inside the tab, the same shape Explore uses for its
  // SCANS/GLOWUPS decks. Here the pair IS the disambiguation — "GIVEN" alone
  // was a fragment (see above), but beside "RECEIVED" it parses, so the sub-
  // switch can use the bare words the top-level strip could not.
  FEEDBACK_RECEIVED_SUBTAB: "RECEIVED",
  FEEDBACK_GIVEN_SUBTAB: "GIVEN",
  FEEDBACK_RECEIVED_EMPTY_HEADLINE: "No feedback yet",
  // Distinct from the line above: this is not "you have no scans", it is "your
  // scans are out there and nobody has read one yet". The scans tab owns the
  // no-scans case.
  FEEDBACK_RECEIVED_SEALED_NOTE:
    "Sealed readings are not listed. Open one and its feedback appears here.",

  // ── Scan sharing (Account) ────────────────────────────────────────────────
  // ── Support (the in-app contact form at the foot of /account) ─────────────
  //
  // The public /contact page keeps its mailto: link — it has no signed-in user
  // to attach. Here we know who is writing, so the form sends through
  // sendSupportMessage and the uid rides along. SUPPORT_DIRECT_NOTE is the
  // escape hatch for the one case the form cannot serve: an account with no
  // email on file has nowhere for a reply to land.
  SUPPORT_BUTTON: "CONTACT SUPPORT",
  SUPPORT_MODAL_EYEBROW: "SUPPORT",
  SUPPORT_MODAL_HEADLINE: "Reach the team",
  SUPPORT_MODAL_BODY:
    "One inbox, read by a person. Tell us what happened and we will write back.",
  SUPPORT_TOPIC_LABEL: "WHAT IS THIS ABOUT",
  // Keyed by SUPPORT_TOPICS. A test asserts an entry exists for every key, so a
  // contract change fails loudly rather than rendering a blank chip.
  SUPPORT_TOPIC_LABELS: {
    account: "Account",
    billing: "Billing",
    scan: "A scan",
    glowup: "A Glowup",
    privacy: "Privacy",
    other: "Something else",
  },
  SUPPORT_MESSAGE_LABEL: "MESSAGE",
  SUPPORT_MESSAGE_PLACEHOLDER: "What happened, and what you expected instead.",
  SUPPORT_SEND: "SEND",
  SUPPORT_CANCEL: "CANCEL",
  SUPPORT_SENT_HEADLINE: "Message sent",
  SUPPORT_SENT_BODY:
    "It is with the team. A reply comes to the address on your account.",
  SUPPORT_SENT_DISMISS: "CLOSE",
  SUPPORT_ERROR_GENERIC: "That message could not be sent. Try again shortly.",
  SUPPORT_ERROR_RATE_LIMITED:
    "That is several messages in one hour. We have the earlier ones and will reply to those.",
  SUPPORT_ERROR_NO_EMAIL:
    "This account has no email address on file, so a reply would have nowhere to go. Write to us directly instead.",
  SUPPORT_DIRECT_NOTE: "Or write to",

  SHARING_LABEL: "SCAN FEEDBACK",
  SHARING_BODY:
    "Other members can give feedback on your scans anonymously, with a bar across the eyes and no handle attached. This is on by default. Turning it off removes your scans from Explore.",
  SHARING_ON: "ALLOWED",
  SHARING_OFF: "PRIVATE",
  SHARING_ERROR: "That preference could not be saved. Try again shortly.",
});
